<template>
  <div class="site-header-desktop container">
    <div class="site-header-desktop__item site-header-desktop__logo">
      <meta
        itemprop="url"
        :content="rootURL"
      >
      <template v-if="logoObject">
        <a
          :href="rootURL"
          class="logo logo--image"
          :aria-label="shopName"
        >
          <img
            itemprop="logo"
            :src="logoSrc"
            :alt="logoObject.alt || shopName"
          >
        </a>
        <a
          v-if="altLogoObject"
          :href="rootURL"
          class="logo logo--image-alt"
          :aria-label="shopName"
        >
          <img
            itemprop="logo"
            :src="altLogoSrc"
            :alt="altLogoObject.alt || shopName"
          >
        </a>
      </template>
      <a
        v-else
        class="logo logo--text"
        :href="rootURL"
        :aria-label="shopName"
        v-html="logoText || shopName"
      />
    </div>
    <desktop-menu
      class="site-header-desktop__item site-header-desktop__menu"
      :main-menu="mainMenu"
      :enable-link-background="enableLinkBackground"
      :enable-megamenu="enableMegamenu"
      :section-blocks="sectionBlocks"
    />
    <div class="site-header-desktop__item site-header-desktop__icons">
      <button
        v-if="enableHeaderSearch"
        type="button"
        class="site-header-desktop__icon site-header-desktop__icon--search search-app__trigger"
        :aria-label="toggleSearchText"
      >
        <icon-search />
      </button>
      <div
        v-if="enableHeaderAccount"
        class="site-header-desktop__icon site-header-desktop__icon--account"
      >
        <component
          :is="isCustomer ? 'a' : 'button'"
          :href="accountURL"
          :aria-label="accountText"
          @click="toggleAccountForm"
        >
          <icon-user />
        </component>
        <transition name="fade">
          <account-form
            v-if="!isCustomer && isAccountFormOpened"
            @on-account-form-close="toggleAccountForm"
          />
        </transition>
      </div>
      <a
        v-if="enableHeaderWishlist"
        :href="wishlistUrl"
        class="site-header-desktop__icon site-header-desktop__icon--wishlist"
        :aria-label="wishlistText"
      >
        <span class="hamburger-menu__bottom-icon">
          <icon-heart />
        </span>
      </a>

      <cart-button
        v-if="enableHeaderCart"
        class="site-header-desktop__icon site-header-desktop__icon--cart"
      />
    </div>
  </div>
</template>

<script>

/**
 * Component: Site Header Desktop
 * -----------------------------------------------------------------------------
 * @param {string}  [liquid.sectionId] - Section's Liquid ID.
 * @param {object}  [logoObject] - A formatted image logo.
 * @param {object}  [altLogoObject] - A formatted alternative image logo.
 * @param {string}  [logoSrc] - Logo file src with responsive size (max-width from settings).
 * @param {string}  [altLogoSrc] - Alternative logo src with responsive size.
 * @param {string}  [logoText] - Text (SVG) setting for header logo.
 * @param {string}  [wishlistUrl] - Link to the Wishlist page.
 * @param {boolean} [enableHeaderAccount] - Setting for enabling header account.
 * @param {boolean} [enableHeaderCart] - Setting for enabling header cart.
 * @param {boolean} [enableHeaderSearch] - Setting for enabling header search.
 * @param {boolean} [enableHeaderWishlist] - Setting for enabling header wishlist.
 * @param {boolean} [enableLinkBackground] - Setting for enabling background styling for highlighted links.
 * @param {boolean} [enableMegamenu] - Setting for enabling header Megamenu.
 * @param {object}  [mainMenu] - Formatted nested linklist.
 * @param {Array}   [sectionBlocks] - Formatted section blocks array.
 * @param {boolean} [isSearchOpened] - Value which indicates the current state of search overlay.
 */

import { defineAsyncComponent } from 'vue'

import CartButton from '~components/site-header-components/cart-button/cart-button'

import DesktopMenu from '~components/site-header-components/desktop-menu/desktop-menu'

import IconHeart from '~icons/header/icon-heart.svg'
import IconSearch from '~icons/header/icon-search.svg'
import IconUser from '~icons/header/icon-user.svg'

export default {
  name: 'SiteHeaderDesktop',

  components: {
    CartButton,
    IconSearch,
    IconHeart,
    IconUser,
    DesktopMenu,
    AccountForm: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.site-header-account-form' */
        '~components/site-header-components/account-form/account-form'
      ),
    }),
  },

  props: {
    logoObject: {
      type: [ Boolean, Object ],
      default: false,
    },
    altLogoObject: {
      type: [ Boolean, Object ],
      default: false,
    },
    logoSrc: {
      type: String,
      default: '',
    },
    altLogoSrc: {
      type: String,
      default: '',
    },
    logoText: {
      type: String,
      default: '',
    },
    wishlistUrl: {
      type: String,
      default: '',
    },
    enableHeaderCart: {
      type: Boolean,
      default: false,
    },
    enableHeaderAccount: {
      type: Boolean,
      default: false,
    },
    enableHeaderSearch: {
      type: Boolean,
      default: false,
    },
    enableHeaderWishlist: {
      type: Boolean,
      default: false,
    },
    enableLinkBackground: {
      type: Boolean,
      default: false,
    },
    enableMegamenu: {
      type: Boolean,
      default: false,
    },
    mainMenu: {
      type: Array,
      default: () => [],
    },
    sectionBlocks: {
      type: Array,
      default: () => [],
    },
    isSearchOpened: {
      type: Boolean,
      default: false,
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
  },

  emits: [ 'on-account-form-toggle' ],

  data() {
    return {
      isAccountFormOpened: false,
    }
  },

  computed: {

    /**
     * Calculate shop root URL for announcement message
     * @returns {string}
     */
    rootURL() {
      return mint.routes.root
    },

    /**
     * Calculate liquid `shop name` to display by default if no logo is set
     * @returns {string}
     */
    shopName() {
      return mint.shop.name
    },

    /**
     * Calculate the 'Toggle Search' button text.
     * @returns {string}
     */
    toggleSearchText() {
      return mint.strings.header.toggleSearch
    },

    /**
     * Calculate if customer is logged in.
     * @returns {string}
     */
    isCustomer() {
      return window.mint.customer.loggedIn
    },

    /**
     * Calculate account URL.
     * @returns {string}
     */
    accountURL() {
      return window.mint.routes.account.url
    },

    /**
     * Calculate account link text.
     * @returns {string}
     */
    accountText() {
      return window.mint.customer.loggedIn
        ? window.mint.strings.account.title
        : window.mint.strings.customer.login
    },

    /**
     * Calculate wishlist link text.
     * @returns {string}
     */
    wishlistText() {
      return window.mint.strings.wishlist.heading
    },
  },

  mounted() {
    this.setEventListeners()
  },

  methods: {

    /**
     * Set event listeners.
     */
    setEventListeners() {

      /**
       * If the cart drawer is opened while the account form is open, close it.
       */
      mint.EventBus.on('cartDrawer:open', () => {
        if (this.isAccountFormOpened) {
          this.toggleAccountForm()
        }
      })
    },

    /**
     * Toggle account form display.
     * @fires `on-account-form-toggle`
     */
    toggleAccountForm() {
      if (this.isCustomer) {
        return
      }

      this.isAccountFormOpened = !this.isAccountFormOpened
      this.$emit('on-account-form-toggle', this.isAccountFormOpened)

      if (this.isAccountFormOpened) {
        mint.EventBus.dispatch('cartDrawer:close')
      }
    },
  },
}
</script>

<style src="./site-header-desktop.scss" lang="scss"></style>
